/**
 * Upper-notice dismissal (in-app design spec § 12 § 3.1 "Upper notice";
 * ENG-APP-UX-TUNEUP Track C question 4, 2026-10-18).
 *
 * A dismissed notice stays away for SOFT_DISMISS_DAYS, or for good after the
 * second dismissal — the rule the Dashboard's Workflows card set (§ 3a item
 * 4). The state is persisted per WORKSPACE in localStorage, never per app: a
 * notice is about the workspace's setup, so switching apps must not
 * resurrect it. `AddRicherSignalsCard` reads and writes through these helpers
 * (the store-console strip is NOT dismissible -- § 12 § 2.3 -- it clears
 * itself on connect). Callers re-read the state in the render body (never seed it into a
 * useState initializer): the shell keeps the notices mounted across app and
 * route changes.
 */
export const SOFT_DISMISS_DAYS = 7;

export type UpperNotice = "add-richer-signals";

export interface DismissState {
  dismissedAt: number;
  count: number;
}

export function upperNoticeKey(
  notice: UpperNotice,
  workspaceId: string,
): string {
  return `upper-notice-${notice}-${workspaceId}`;
}

export function readDismissState(key: string): DismissState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DismissState>;
    if (typeof parsed.dismissedAt !== "number") return null;
    if (typeof parsed.count !== "number") return null;
    return { dismissedAt: parsed.dismissedAt, count: parsed.count };
  } catch {
    return null;
  }
}

export function writeDismissState(key: string, state: DismissState): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // localStorage may be unavailable (private mode, quota); the notice
    // simply re-appears on the next render. No telemetry yet.
  }
}

export function isWithinSoftDismiss(state: DismissState | null): boolean {
  if (!state) return false;
  if (state.count >= 2) return true;
  const elapsedMs = Date.now() - state.dismissedAt;
  return elapsedMs < SOFT_DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

/** Records one dismissal (the second one is permanent). */
export function dismissUpperNotice(key: string): void {
  const prior = readDismissState(key);
  writeDismissState(key, {
    dismissedAt: Date.now(),
    count: (prior?.count ?? 0) + 1,
  });
}
